import React, { useEffect, useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Minimize2, Maximize2, Video } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface WebcamMonitoringPreviewProps {
  stream: MediaStream | null;
  isActive: boolean;
  isCapturing: boolean;
  isBackgroundRecording?: boolean;
  monitoringMethod?: 'live' | 'background';
}

export function WebcamMonitoringPreview({ 
  stream, 
  isActive, 
  isCapturing, 
  isBackgroundRecording = false,
  monitoringMethod = 'live'
}: WebcamMonitoringPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const isMobile = useIsMobile();

  // Set video stream
  useEffect(() => {
    if (videoRef.current && stream) {
      console.log('[WebcamPreview] Setting video source:', stream.id);
      videoRef.current.srcObject = stream;
      
      // Ensure video plays
      videoRef.current.play().catch(err => {
        console.error('[WebcamPreview] Error playing video:', err);
      });
    } else if (videoRef.current && !stream) {
      console.log('[WebcamPreview] Clearing video source');
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  // Debug video state
  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      const handleLoadedMetadata = () => {
        console.log('[WebcamPreview] Video metadata loaded:', {
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          readyState: video.readyState
        });
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    }
  }, [stream]);

  // Show preview when active, even if stream isn't immediately available
  if (!isActive) {
    console.log('[WebcamPreview] Not rendering - not active');
    return null;
  }

  const previewSize = isMobile 
    ? (isMinimized ? 'w-12 h-9' : 'w-32 h-24')
    : (isMinimized ? 'w-16 h-12' : 'w-48 h-36');

  return (
    <Card className={`fixed ${isMobile ? 'top-4 right-4' : 'top-6 right-6'} ${previewSize} z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border shadow-lg overflow-hidden transition-all duration-300 ease-in-out`}>
      {!isMinimized && (
        <div className="relative w-full h-full">
          {/* Video preview or fallback */}
          {stream ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              data-webcam-preview
              className="w-full h-full object-cover rounded-sm"
            />
          ) : (
            <div className="w-full h-full bg-secondary/20 rounded-sm flex items-center justify-center">
              <div className="text-center">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-1">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                </div>
                <p className="text-xs text-muted-foreground">Webcam Active</p>
              </div>
            </div>
          )}
          
          {/* Recording indicator */}
          {(isCapturing || isBackgroundRecording) && (
            <div className="absolute top-1 left-1 flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full animate-pulse ${
                monitoringMethod === 'background' ? 'bg-orange-500' : 'bg-red-500'
              }`} />
              {!isMobile && (
                <span className="text-xs text-white bg-black/50 px-1 rounded">
                  {monitoringMethod === 'background' ? 'BG' : 'REC'}
                </span>
              )}
            </div>
          )}
          
          {/* Controls overlay */}
          <div className="absolute top-1 right-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(true)}
              className="h-6 w-6 p-0 bg-black/20 hover:bg-black/40 text-white border-0"
            >
              <Minimize2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
      
      {isMinimized && (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMinimized(false)}
            className="h-full w-full p-0 flex flex-col items-center justify-center gap-1"
          >
            <Video className="h-3 w-3" />
            {(isCapturing || isBackgroundRecording) && (
              <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                monitoringMethod === 'background' ? 'bg-orange-500' : 'bg-red-500'
              }`} />
            )}
          </Button>
        </div>
      )}
    </Card>
  );
}